import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { graphql } from '~/apollo/generated/v4';
import {
  BookmarkParentType,
  BookmarkTargetType,
} from '~/apollo/generated/v4/graphql';
import { NotFound } from '~/components/common/NotFound';
import { PictureThumbnailV4 } from '~/components/common/picture-thumbnail-v4';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useShowUnpublished } from '~/components/upload/supporting-object/use-show-unpublished';
import { useRouteParam } from '~/hooks/routing';
import { createRefetchQueries } from '~/utils/graphql';
import { soBookmarkPath } from '~/utils/modules/bookmark';
import {
  countUnpublishedItems,
  isPublished,
} from '~/utils/modules/supporting-object';

const STUDY_PICTURES_ROUTE = graphql(`
  query StudyPicturesRoute($id: ID!) {
    studyGet(id: $id) {
      id
      pictures {
        ...soPictureParts
      }
    }
  }
`);

export default function StudyPicturesRoute() {
  return (
    <ApolloProviderV4>
      <StudyPicturesRouteInner />
    </ApolloProviderV4>
  );
}

function StudyPicturesRouteInner() {
  const studyId = useRouteParam('studyId', parseInt);

  const { data, loading } = useQuery(STUDY_PICTURES_ROUTE, {
    variables: { id: studyId },
  });

  const refetchQueries = createRefetchQueries([
    { query: STUDY_PICTURES_ROUTE, variables: { id: studyId } },
  ]);

  const study = data?.studyGet;
  const allPictures = study?.pictures ?? [];

  const { showUnpublished, PublishedToggle } = useShowUnpublished(
    countUnpublishedItems(allPictures),
  );

  const pictures = allPictures
    .filter(picture => showUnpublished || isPublished(picture))
    .sort(R.ascend(picture => picture.priority ?? Infinity));

  if (loading) return <SpinnerPlaceholder />;
  if (!study) return <NotFound />;

  return (
    <div className="space-y-4">
      <PublishedToggle />

      <div className="grid md:grid-cols-2 gap-4">
        {pictures.map(picture => (
          <div key={picture.id}>
            <PictureThumbnailV4
              picture={picture}
              imgSrc={picture.pictureUrl}
              thumbnailImgSrc={picture.thumbnailUrl}
              bookmarkable={{
                parentType: BookmarkParentType.Study,
                parentId: studyId,
                targetType: BookmarkTargetType.Picture,
                targetId: parseInt(picture.id),
                path: soBookmarkPath(
                  'study',
                  studyId,
                  BookmarkParentType.Study,
                  picture.outcropTagId,
                ),
              }}
              refetchQueries={refetchQueries}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
